import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  Loader2,
  LayoutDashboard,
  ListChecks,
  BarChart3,
  Users as UsersIcon,
  UserMinus,
  Calculator,
  CreditCard,
  Phone,
  FileText,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../components/Toast";
import ConfirmDeleteDrawer from "../../components/ConfirmDeleteDrawer";
import StudentAccessCard from "../../components/StudentAccessCard";
import StudentAccessTimeline from "../../components/StudentAccessTimeline";
import { getAccessBadge } from "../../lib/accessStatus";
import OverviewTab from "./student-tabs/OverviewTab";
import ActivitiesTab from "./student-tabs/ActivitiesTab";
import MetricsTab from "./student-tabs/MetricsTab";
import LeadsTab from "./student-tabs/LeadsTab";
import BajasTab from "./student-tabs/BajasTab";
import SimulationsTab from "./student-tabs/SimulationsTab";
import PaymentsTab from "./student-tabs/PaymentsTab";
import SessionsTab from "./student-tabs/SessionsTab";
import DocumentsTab from "./student-tabs/DocumentsTab";

const TABS = [
  { id: "overview", label: "Resumen", icon: LayoutDashboard },
  { id: "activities", label: "Actividades", icon: ListChecks },
  { id: "payments", label: "Pagos", icon: CreditCard },
  { id: "sessions", label: "Sesiones", icon: Phone },
  { id: "documents", label: "Documentos", icon: FileText },
  { id: "metrics", label: "Métricas", icon: BarChart3 },
  { id: "leads", label: "Leads", icon: UsersIcon },
  { id: "bajas", label: "Bajas", icon: UserMinus },
  { id: "simulations", label: "Simulaciones", icon: Calculator },
];

export default function StudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(true);
  const [airtableLoaded, setAirtableLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [student, setStudent] = useState(null);
  const [data, setData] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchSupabase = async () => {
    setAirtableLoaded(false);
    const [
      studentRes,
      templatesRes,
      progressRes,
      monthlyRes,
      financesRes,
      configRes,
      leadsRes,
      bajasRes,
      simsRes,
      followupsRes,
      loginsRes,
      documentsRes,
      leadFormRes,
    ] = await Promise.all([
      supabase.from("students").select("*").eq("id", studentId).single(),
      supabase
        .from("activity_templates")
        .select("*")
        .eq("is_active", true)
        .order("block_order")
        .order("sort_order"),
      supabase.from("student_progress").select("*").eq("student_id", studentId),
      supabase
        .from("monthly_data")
        .select("*")
        .eq("student_id", studentId)
        .order("year", { ascending: false })
        .order("month", { ascending: false }),
      supabase
        .from("monthly_finances")
        .select("*")
        .eq("student_id", studentId)
        .order("year", { ascending: false })
        .order("month", { ascending: false }),
      supabase
        .from("metrics_config")
        .select("*")
        .eq("student_id", studentId)
        .maybeSingle(),
      supabase
        .from("leads")
        .select("*")
        .eq("student_id", studentId)
        .order("created_at", { ascending: false }),
      supabase
        .from("bajas")
        .select("*")
        .eq("student_id", studentId)
        .order("created_at", { ascending: false }),
      supabase
        .from("simulations")
        .select("*")
        .eq("student_id", studentId)
        .order("created_at", { ascending: false }),
      supabase
        .from("client_followups")
        .select("id, completed")
        .eq("student_id", studentId),
      supabase
        .from("login_history")
        .select("logged_in_at")
        .eq("student_id", studentId)
        .order("logged_in_at", { ascending: true }),
      supabase
        .from("documents")
        .select("*")
        .eq("student_id", studentId)
        .order("created_at", { ascending: false }),
      supabase
        .from("lead_capture_forms")
        .select("slug, is_active, views_count, submissions_count")
        .eq("student_id", studentId)
        .maybeSingle(),
    ]);

    if (studentRes.error) {
      showError("Alumno no encontrado");
      navigate("/admin/alumnos");
      return;
    }

    const progressMap = new Map(
      (progressRes.data || []).map((p) => [p.template_id, p])
    );

    const activities = (templatesRes.data || []).map((t) => {
      const p = progressMap.get(t.id);
      return {
        ...t,
        completed: p?.completed || false,
        completed_at: p?.completed_at || null,
        updated_at: p?.updated_at || null,
        response: p?.response || null,
        notes: p?.notes || null,
      };
    });

    const stu = studentRes.data;
    const isAirtable = stu.airtable_id && !stu.airtable_id.startsWith("admin_");

    setStudent(stu);
    setData({
      activities,
      monthlyData: monthlyRes.data || [],
      finances: financesRes.data || [],
      metricsConfig: configRes.data || null,
      leads: leadsRes.data || [],
      bajas: bajasRes.data || [],
      simulations: simsRes.data || [],
      followups: followupsRes.data || [],
      logins: loginsRes.data || [],
      documents: documentsRes.data || [],
      leadForm: leadFormRes?.data || null,
      payments: [],
      sessions: [],
      isAirtable,
    });
    setLoading(false);

    return stu;
  };

  // Phase 2: Load Airtable data in the background (slower)
  const fetchAirtable = async (stu) => {
    if (!stu) return;
    const isAirtable = stu.airtable_id && !stu.airtable_id.startsWith("admin_");

    let payments = [];
    let sessions = [];

    if (isAirtable) {
      const atId = stu.airtable_id;
      const [payRes, sesRes] = await Promise.all([
        supabase.functions
          .invoke("airtable-proxy", { body: { table: "pagos", airtable_id: atId } })
          .catch((e) => { console.warn("[pagos] proxy error:", e); return { data: null, error: e }; }),
        supabase.functions
          .invoke("airtable-proxy", { body: { table: "sesiones", airtable_id: atId } })
          .catch((e) => { console.warn("[sesiones] proxy error:", e); return { data: null, error: e }; }),
      ]);
      if (payRes.error) console.warn("[pagos] airtable-proxy:", payRes.error);
      if (sesRes.error) console.warn("[sesiones] airtable-proxy:", sesRes.error);
      payments = payRes?.data?.data ?? [];
      sessions = sesRes?.data?.data ?? [];
    }

    if (payments.length === 0) {
      const { data: payData } = await supabase
        .from("payments")
        .select("*")
        .eq("student_id", stu.id)
        .order("date", { ascending: false });
      payments = payData || [];
    }
    if (sessions.length === 0) {
      const { data: sesData } = await supabase
        .from("sessions")
        .select("*")
        .eq("student_id", stu.id)
        .order("date", { ascending: false });
      sessions = sesData || [];
    }

    setData((prev) => (prev ? { ...prev, payments, sessions } : prev));
    setAirtableLoaded(true);
  };

  useEffect(() => {
    fetchSupabase().then(fetchAirtable);
  }, [studentId]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-student`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ student_id: studentId }),
        }
      );

      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error);

      showSuccess("Estudiante eliminado");
      navigate("/admin/alumnos");
    } catch (err) {
      showError(err.message || "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!student || !data) return null;

  const tabContent = {
    overview: <OverviewTab student={student} data={data} />,
    activities: (
      <ActivitiesTab
        activities={data.activities}
        studentId={studentId}
        studentName={student.name}
        onRefetch={fetchSupabase}
      />
    ),
    metrics: (
      <MetricsTab
        monthlyData={data.monthlyData}
        finances={data.finances}
        config={data.metricsConfig}
      />
    ),
    leads: <LeadsTab leads={data.leads} leadForm={data.leadForm} />,
    bajas: <BajasTab bajas={data.bajas} />,
    simulations: <SimulationsTab simulations={data.simulations} />,
    payments: (
      <PaymentsTab payments={data.payments} isAirtable={data.isAirtable} loading={!airtableLoaded} />
    ),
    sessions: (
      <SessionsTab sessions={data.sessions} isAirtable={data.isAirtable} loading={!airtableLoaded} />
    ),
    documents: <DocumentsTab documents={data.documents} />,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin/alumnos")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {student.name}
            </h1>
            <p className="text-gray-500">{student.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
            {student.stage || "Activo"}
          </span>
          <span
            className={`px-2.5 py-1 text-xs font-medium rounded-full ${getAccessBadge(student).color}`}
            title="Estado de acceso"
          >
            {getAccessBadge(student).label}
          </span>
          <button
            onClick={() => setShowDelete(true)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar estudiante"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Acceso con caducidad (Fase 5) */}
      <StudentAccessCard student={student} onUpdated={setStudent} />

      {/* Historial de acceso (Fase 6.5) */}
      <StudentAccessTimeline student={student} />

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {tabContent[activeTab]}

      {showDelete && (
        <ConfirmDeleteDrawer
          title="Eliminar estudiante"
          itemName={student.name}
          message="Se eliminarán permanentemente todos los datos del estudiante: progreso, simulaciones, leads y documentos."
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
}
